import { useState, useEffect, useRef } from 'react';
import {
    Modal,
    ModalBody,
    Button,
    Text,
    Stack,
    Avatar,
    Badge,
    IconSignIn,
    IconSignOut,
    IconMugSaucer
} from '@7shifts/sous-chef-base';
import type { SuccessResponse } from '@src/api/punchTypes';
import { useTranslation } from '@src/i18n';
import { formatInstant } from './timeUtils';
import './PunchResultModal.css';

const DEFAULT_AUTO_DISMISS_MS = 5000;

type PunchIcon = typeof IconSignIn;

interface PunchResultModalProps {
    result: SuccessResponse;
    firstName: string;
    onDismiss: () => void;
    autoDismissMs?: number;
}

export function PunchResultModal({
    result,
    firstName,
    onDismiss,
    autoDismissMs = DEFAULT_AUTO_DISMISS_MS
}: PunchResultModalProps) {
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;
    const { t } = useTranslation();

    const [remainingMs, setRemainingMs] = useState(autoDismissMs);

    useEffect(() => {
        const start = Date.now();
        const id = setInterval(() => {
            const left = Math.max(0, autoDismissMs - (Date.now() - start));
            setRemainingMs(left);
            if (left <= 0) {
                clearInterval(id);
                onDismissRef.current();
            }
        }, 1000);
        return () => clearInterval(id);
    }, [autoDismissMs]);

    const presentation: Record<
        SuccessResponse['punchType'],
        { heading: string; subtitle: string; Icon: PunchIcon }
    > = {
        clock_in: {
            heading: t('punchResult.clockIn.heading'),
            subtitle: t('punchResult.clockIn.subtitle', { name: firstName }),
            Icon: IconSignIn
        },
        clock_out: {
            heading: t('punchResult.clockOut.heading'),
            subtitle: t('punchResult.clockOut.subtitle', { name: firstName }),
            Icon: IconSignOut
        },
        break_start: {
            heading: t('punchResult.breakStart.heading'),
            subtitle: t('punchResult.breakStart.subtitle', { name: firstName }),
            Icon: IconMugSaucer
        },
        break_end: {
            heading: t('punchResult.breakEnd.heading'),
            subtitle: t('punchResult.breakEnd.subtitle', { name: firstName }),
            Icon: IconSignIn
        }
    };

    const secs = Math.ceil(remainingMs / 1000);
    const countdown = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(
        2,
        '0'
    )}`;

    const { heading, subtitle, Icon } = presentation[result.punchType];

    return (
        <Modal testId="punch-result-modal">
            <ModalBody>
                <Stack space={20}>
                    <Stack space={20} alignItems="center">
                        <Avatar
                            size="extra-large"
                            url={result.userPhotoUrl ?? undefined}
                            alt=""
                            badge={
                                <Badge theme="success">
                                    <Icon size="small" />
                                </Badge>
                            }
                        />
                        <Stack space={4} alignItems="center">
                            <Text as="h1" alignment="center">
                                {heading}
                            </Text>
                            <Text
                                as="h4"
                                alignment="center"
                                color="var(--color-surface-on-color-subtle)"
                            >
                                {subtitle}
                            </Text>
                        </Stack>
                    </Stack>
                    <div
                        className="punch-result-modal__time"
                        data-testid="punch-result-time"
                    >
                        <Text as="insight" alignment="center">
                            {formatInstant(result.punchedAt)}
                        </Text>
                    </div>
                    <Button
                        type="button"
                        theme="primary"
                        size="full-width"
                        onClick={onDismiss}
                        testId="punch-result-dismiss"
                    >
                        {t('punchResult.ok', { countdown })}
                    </Button>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
